import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import { OnboardingService } from '../services/onboarding.service';
import type { OnboardingProgress, OnboardingConfig, UserPreferences } from '../../../shared/types/onboarding';

export const useOnboarding = () => {
  const { user } = useAuth();
  
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [config, setConfig] = useState<OnboardingConfig>(OnboardingService.getConfig());
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);


  // Cargar datos iniciales
  const loadData = useCallback(async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Cargar progreso existente o inicializar
      let userProgress = OnboardingService.getProgress(user.uid);
      if (!userProgress) {
        userProgress = OnboardingService.initializeOnboarding(user.uid);
      }
      setProgress(userProgress);

      // Cargar preferencias existentes o crear por defecto
      let preferences = OnboardingService.getUserPreferences(user.uid);
      if (!preferences) {
        preferences = OnboardingService.createDefaultPreferences(user.uid);
      }
      setUserPreferences(preferences);

      console.log('Datos de onboarding cargados:', {
        hasProgress: !!userProgress,
        isCompleted: userProgress?.isCompleted,
        currentStep: userProgress?.currentStep
      });

    } catch (error) {
      console.error('Error cargando datos de onboarding:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  // Completar paso
  const completeStep = useCallback(async (stepId: string) => {
    if (!user?.uid) return false;

    try {
      const updatedProgress = OnboardingService.completeStep(user.uid, stepId);
      if (updatedProgress) {
        setProgress(updatedProgress);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error completando paso:', error);
      return false;
    }
  }, [user?.uid]);

  // Saltar paso
  const skipStep = useCallback(async (stepId: string) => {
    if (!user?.uid) return false;

    try {
      const updatedProgress = OnboardingService.skipStep(user.uid, stepId);
      if (updatedProgress) {
        setProgress(updatedProgress);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saltando paso:', error);
      return false;
    }
  }, [user?.uid]);

  // Ir al paso anterior
  const goToPreviousStep = useCallback(() => {
    if (!progress || progress.currentStep <= 0) return false;

    try {
      const updatedProgress = { ...progress, currentStep: progress.currentStep - 1 };
      OnboardingService.saveProgress(updatedProgress);
      setProgress(updatedProgress);
      return true;
    } catch (error) {
      console.error('Error yendo al paso anterior:', error);
      return false;
    }
  }, [progress]);

  // Ir al paso siguiente
  const goToNextStep = useCallback(() => {
    if (!progress) return false;

    try {
      const updatedProgress = { ...progress, currentStep: progress.currentStep + 1 };
      OnboardingService.saveProgress(updatedProgress);
      setProgress(updatedProgress);
      return true;
    } catch (error) {
      console.error('Error yendo al paso siguiente:', error);
      return false;
    }
  }, [progress]);

  // Completar onboarding
  const completeOnboarding = useCallback(async () => {
    if (!user?.uid) return false;

    try {
      const updatedProgress = OnboardingService.completeOnboarding(user.uid);
      if (updatedProgress) {
        setProgress(updatedProgress);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error completando onboarding:', error);
      return false;
    }
  }, [user?.uid]);

  // Guardar preferencias de usuario
  const saveUserPreferences = useCallback(async (preferences: Partial<UserPreferences>) => {
    if (!user?.uid || !userPreferences) return false;

    try {
      const updatedPreferences = { ...userPreferences, ...preferences };
      OnboardingService.saveUserPreferences(updatedPreferences);
      setUserPreferences(updatedPreferences);
      return true;
    } catch (error) {
      console.error('Error guardando preferencias:', error);
      return false;
    }
  }, [user?.uid, userPreferences]);

  // Reiniciar onboarding
  const resetOnboarding = useCallback(() => {
    if (!user?.uid) return false;

    try {
      OnboardingService.resetOnboarding(user.uid);
      const newProgress = OnboardingService.initializeOnboarding(user.uid);
      setProgress(newProgress);
      return true;
    } catch (error) {
      console.error('Error reiniciando onboarding:', error);
      return false;
    }
  }, [user?.uid]);

  // Actualizar configuración
  const updateConfig = useCallback((newConfig: Partial<OnboardingConfig>) => {
    try {
      const updatedConfig = { ...config, ...newConfig };
      OnboardingService.saveConfig(updatedConfig);
      setConfig(updatedConfig);
      return true;
    } catch (error) {
      console.error('Error actualizando configuración:', error);
      return false;
    }
  }, [config]);

  // Verificar si debe mostrar onboarding
  const shouldShowOnboarding = useCallback(() => {
    if (!user?.uid || !progress) return false;
    return !progress.isCompleted && config.showWelcome;
  }, [user?.uid, progress, config.showWelcome]);

  // Obtener paso actual
  const getCurrentStep = useCallback(() => {
    return progress?.currentStep || 0;
  }, [progress]);

  // Verificar si un paso está completado
  const isStepCompleted = useCallback((stepId: string) => {
    return progress?.completedSteps.includes(stepId) || false;
  }, [progress]);

  // Verificar si un paso fue saltado
  const isStepSkipped = useCallback((stepId: string) => {
    return progress?.skippedSteps.includes(stepId) || false;
  }, [progress]);

  // Calcular progreso general
  const getProgressPercentage = useCallback((totalSteps: number) => {
    if (!progress || totalSteps === 0) return 0;
    return Math.round((progress.completedSteps.length / totalSteps) * 100);
  }, [progress]);

  // Cargar datos cuando cambie el usuario
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    // Estado
    progress,
    config,
    userPreferences,
    loading,

    
    // Acciones
    completeStep,
    skipStep,
    goToPreviousStep,
    goToNextStep,
    completeOnboarding,
    saveUserPreferences,
    resetOnboarding,
    updateConfig,
    loadData,
    
    // Helpers
    shouldShowOnboarding,
    getCurrentStep,
    isStepCompleted,
    isStepSkipped,
    getProgressPercentage,
    
    // Estado calculado
    isCompleted: progress?.isCompleted || false,
    hasStarted: !!progress && progress.completedSteps.length > 0,
    canGoBack: progress ? progress.currentStep > 0 : false,
    canSkip: config.allowSkip
  };
};